import Vue from 'vue'
import Vuex from 'vuex'
import repositories from '@/repositories/RepositoryFactory'
import CUSTOMER_SEGMENTS from '@/const/customersegments'
import EVALUATIONS from '@/const/evaluations'
import AGES from '@/const/ages'
import { getLabels } from '@/util/labels'
import {
  SET_SERVICE_STATUS,
  SET_ACCOUNT,
  SET_COMPANIES,
  SET_ITEM_CATEGORY_LEVELS,
  SET_CALENDAR,
  SET_ANNOUNCEMENT,
  ENABLE_SEASON_FEATURE,
  SET_OUTPUT_COLUMNS,
} from '@/const/mutation-types'
import { LOAD_BASIC_MASTERS } from '@/const/action-types'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // サービス稼働状態
    service: {
      available: false,
      message: undefined,
    },
    // アカウント情報
    account: {
      userId: undefined,
      roles: [],
    },
    // データ保持期間マスタ
    calendar: {
      minDate: undefined,
      maxDate: undefined,
    },
    // 企業マスタ（ログインユーザーが参照可能な企業のみ）
    companies: [],
    // 分類階層マスタ（ログインユーザーが参照可能な企業毎の階層すべて）
    // Storeに入れるにはアレですが、、企業コードをキーに階層配列を保存します。
    // {
    //   cyberlinks: [ ...levels ],
    // }
    itemCategoryLevels: {},
    // お知らせ
    announcement: {
      hasUnread: false,
    },
    // 個人設定
    preferences: {
      enableSeasonFeature: false,
    },
    queryMetadata: {
      outputColumns: [],
    },
    // 企業毎変わるようなラベルです
    // .envの定義を参照し、なければデフォルト値を設定します。
    labels: {
      customerSegment: getLabels(
        CUSTOMER_SEGMENTS,
        {
          rank: 'ランク',
          pfv: 'PFV',
          age: '年代',
        },
        'VUE_APP_LABEL_CUSTOMER_SEGMENT',
      ),
      evaluations: getLabels(
        EVALUATIONS,
        {
          amount: '金額',
          qty: '点数',
          receipt_count: '客数',
          customer_count: 'ID数',
          amount_per_qty: '点単価',
          qty_per_receipt: '客点数',
          amount_per_receipt: '客単価',
          receipt_per_customer: 'ID回数',
          qty_per_customer: 'ID点数',
          amount_per_customer: 'ID金額',
          total_amount: '総金額',
          total_qty: '総点数',
          total_receipt_count: '総客数',
        },
        'VUE_APP_LABEL_EVALUATIONS',
      ),
      ages: getLabels(
        AGES,
        {
          '01': '２０歳未満',
          '02': '２０代',
          '03': '３０代',
          '04': '４０代',
          '05': '５０代',
          '06': '６０代',
          '07': '７０歳以上',
          '90': 'その他',
        },
        'VUE_APP_LABEL_AGES',
      ),
    },
  },
  getters: {
    /**
     * ログインユーザーが管理者権限を持つか
     */
    isAdmin: state => {
      if (!state.account.roles) {
        return false
      }
      return state.account.roles.includes('admin')
    },
  },
  mutations: {
    [SET_SERVICE_STATUS](state, { available, message }) {
      state.service.available = available
      state.service.message = message
    },
    [SET_ACCOUNT](state, { userId, companyCode, loginId, roles }) {
      state.account.userId = userId
      state.account.companyCode = companyCode
      state.account.loginId = loginId
      state.account.roles = roles
    },
    [SET_COMPANIES](state, companies) {
      state.companies = companies
    },
    [SET_ITEM_CATEGORY_LEVELS](state, itemCategoryLevels) {
      state.itemCategoryLevels = itemCategoryLevels
    },
    [SET_CALENDAR](state, { from, to }) {
      state.calendar.minDate = from
      state.calendar.maxDate = to
    },
    [SET_ANNOUNCEMENT](state, { hasUnread }) {
      state.announcement.hasUnread = hasUnread
    },
    [ENABLE_SEASON_FEATURE](state, value) {
      state.preferences.enableSeasonFeature = value
    },
    [SET_OUTPUT_COLUMNS](state, outputColumns) {
      state.queryMetadata.outputColumns = outputColumns
    },
  },
  actions: {
    /**
     * 共通で必要となる（Storeに保存する）マスタをロードします。
     */
    async [LOAD_BASIC_MASTERS]({ commit }, { companyCode, loginId }) {
      // アカウント情報
      const account = await repositories
        .get('account')
        .get(companyCode, loginId)
      commit(SET_ACCOUNT, account)

      // 企業一覧
      const companies = await repositories.get('company').get()
      commit(SET_COMPANIES, companies)

      // 分類階層（階層マスタは企業毎）
      const itemCategoryLevels = {}
      for (const { code } of companies) {
        const levels = await repositories.get('itemCategoryLevel').get(code)
        itemCategoryLevels[code] = levels
      }
      commit(SET_ITEM_CATEGORY_LEVELS, itemCategoryLevels)

      // データ保持期間
      const calendar = await repositories.get('calendar').get()
      commit(SET_CALENDAR, calendar)

      // 出力項目
      const outputColumns = await repositories
        .get('queryMetadata')
        .getOutputColumns()
      commit(SET_OUTPUT_COLUMNS, outputColumns)
    },
  },
  // https://vuex.vuejs.org/ja/guide/strict.html
  strict: process.env.NODE_ENV === 'development',
})
